import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload } from 'lucide-react';

export const CompanyForm = ({ visible, onHide, onSave, company }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    raison_social: '',
    email: '',
    phone: '',
    address: '',
    ice: '',
    rib: '',
    manager_first_name: '',
    manager_last_name: '',
    manager_email: '',
    manager_phone: '',
    client_type: '',
    logo: null
  });
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    if (company) {
      setFormData(company);
      setLogoPreview(company.logo);
    } else {
      setFormData({
        raison_social: '',
        email: '',
        phone: '',
        address: '',
        ice: '',
        rib: '',
        manager_first_name: '',
        manager_last_name: '',
        manager_email: '',
        manager_phone: '',
        client_type: '',
        logo: null
      });
      setLogoPreview(null);
    }
  }, [company]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prevData => ({ ...prevData, logo: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      formDataToSend.append(key, formData[key]);
    });
    onSave(formDataToSend);
  };

  const clientTypeOptions = [
    { label: t('assurances'), value: 'ASSURANCES' },
    { label: t('loueure_lld'), value: 'LOUEURE LLD' },
    { label: t('multinational'), value: 'MULTINATIONAL' },
    { label: t('reparateur'), value: 'REPARATEUR' },
    { label: t('pme'), value: 'PME' },
    { label: t('location_de_voiture'), value: 'LOCATION DE VOITURE' },
    { label: t('convention_rh'), value: 'CONVENTION RH' },
    { label: t('proprietaire'), value: 'PROPRIETAIRE' }
  ];

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 pt-20 pb-20 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-8">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">{company ? t('edit_company') : t('new_company')}</h2>
          <button onClick={onHide} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="raison_social" className="block text-sm font-medium text-gray-700">{t('raison_social')}</label>
                <input
                  type="text"
                  id="raison_social"
                  name="raison_social"
                  value={formData.raison_social}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">{t('email')}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">{t('phone')}</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700">{t('address')}</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor="ice" className="block text-sm font-medium text-gray-700">{t('ice')}</label>
                <input
                  type="text"
                  id="ice"
                  name="ice"
                  value={formData.ice}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor="rib" className="block text-sm font-medium text-gray-700">{t('rib')}</label>
                <input
                  type="text"
                  id="rib"
                  name="rib"
                  value={formData.rib}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label htmlFor="client_type" className="block text-sm font-medium text-gray-700">{t('client_type')}</label>
                <select
                  id="client_type"
                  name="client_type"
                  value={formData.client_type}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('select_client_type')}</option>
                  {clientTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              {!company?.id && (
                <>
                  <div>
                    <label htmlFor="manager_first_name" className="block text-sm font-medium text-gray-700">{t('manager_first_name')}</label>
                    <input
                      type="text"
                      id="manager_first_name"
                      name="manager_first_name"
                      value={formData.manager_first_name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="manager_last_name" className="block text-sm font-medium text-gray-700">{t('manager_last_name')}</label>
                    <input
                      type="text"
                      id="manager_last_name"
                      name="manager_last_name"                
                      value={formData.manager_last_name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="manager_email" className="block text-sm font-medium text-gray-700">{t('manager_email')}</label>
                    <input
                      type="email"
                      id="manager_email"
                      name="manager_email"                
                      value={formData.manager_email}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="manager_phone" className="block text-sm font-medium text-gray-700">{t('manager_phone')}</label>
                    <input
                      type="tel"
                      id="manager_phone"
                      name="manager_phone"        
                      value={formData.manager_phone}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </>
              )}
              <div>
                <label htmlFor="logo" className="block text-sm font-medium text-gray-700">{t('company_logo')}</label>
                <div className="mt-1 flex items-center">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Company Logo" className="w-20 h-20 object-cover rounded-md mr-4" />
                  ) : (
                    <div className="w-20 h-20 bg-gray-200 rounded-md flex items-center justify-center mr-4">
                      <Upload className="text-gray-400" size={24} />
                    </div>
                  )}
                  <label htmlFor="logo-upload" className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                    {t('choose_logo')}
                  </label>
                  <input
                    id="logo-upload"
                    name="logo"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="sr-only"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onHide}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

